import { createSelector } from 'reselect';

const getStdFilesPriority = (state) => state.results.stdFilesPriority;
const getStdFiles = (state) => state.results.stdFiles;
const getReportingPeaks = (state) => state.results.reportingPeaks;
const getAreaUnderPeaks = (state) => state.results.areaUnderPeaks;
const getAreaUnderPeaksQc = (state) => state.results.areaUnderPeaksQc;
const getAreaUnderPeaksUnk = (state) => state.results.areaUnderPeaksUnk;
const getPeaksObject = (state) => state.results.peaksObject;
const getQcFiles = (state) => state.results.qcFiles;
const getUnkFiles = (state) => state.results.unkFiles;
export const getAllReportingPeaks = (state) => state.results.allReportingPeaks;
export const getStdXPeaks = (state) => state.results.stdXPeaks;
export const getQcXPeaks = (state) => state.results.qcXPeaks;
export const getUnkXPeaks = (state) => state.results.unkXPeaks;

export const selectMaximumStandardName = createSelector(
  [getStdFilesPriority],
  (stdFilesPriority) => stdFilesPriority.slice(-1)[0]
);

export const selectChartPreviewData = createSelector(
  [getStdFiles, selectMaximumStandardName],
  (stdFiles, maximumStandardName) => {
    const file = stdFiles[maximumStandardName];
    // Handle both old and new file structure
    const fileData = file?.fileData || file;
    return fileData?.originalData || [];
  }
);

export const selectReportingPeaksArray = createSelector(
  [getReportingPeaks],
  (reportingPeaks) => Object.values(reportingPeaks).flat()
);

export const selectAllStdFilesData = createSelector(
  [getStdFilesPriority, getStdFiles],
  (stdFilesPriority, stdFiles) =>
    stdFilesPriority.map((priority) => {
      const file = stdFiles[priority];
      // Handle both old and new file structure
      const fileData = file?.fileData || file;
      return {
        [priority]: fileData?.originalData || []
      };
    })
);

export const selectAreaUnderPeaks = createSelector(
  [getAreaUnderPeaks],
  (areaUnderPeaks) => Object.values(areaUnderPeaks).flat()
);

export const selectAreaUnderPeaksQc = createSelector(
  [getAreaUnderPeaksQc],
  (areaUnderPeaksQc) => Object.values(areaUnderPeaksQc).flat()
);

export const selectAreaUnderPeaksUnk = createSelector(
  [getAreaUnderPeaksUnk],
  (areaUnderPeaksUnk) => Object.values(areaUnderPeaksUnk).flat()
);

export const selectPeaksObject = createSelector(
  [getPeaksObject],
  (peaksObject) => Object.values(peaksObject).flat()
);

export const selectQcFilesData = createSelector(
  [getQcFiles],
  (qcFiles) =>
    Object.entries(qcFiles).map(([key, file]) => {
      // Handle both old and new file structure
      const fileData = file?.fileData || file;
      return {
        [key]: fileData?.originalData || []
      };
    })
);

export const selectUnkFilesData = createSelector(
  [getUnkFiles],
  (unkFiles) =>
    Object.entries(unkFiles).map(([key, file]) => {
      // Handle both old and new file structure
      const fileData = file?.fileData || file;
      return {
        [key]: fileData?.originalData || []
      };
    })
);

export const selectStdFilesData = createSelector(
  [getStdFiles],
  (stdFiles) =>
    Object.entries(stdFiles).map(([key, file]) => ({
      [key]: file.originalData || []
    }))
);


export const selectStdFilesResultData = createSelector(
  [getStdFiles],
  (stdFiles) => {
    const resultData = {};

    Object.keys(stdFiles).forEach((standardKey) => {
      // Handle both old and new file structure
      const fileData = stdFiles[standardKey].fileData || stdFiles[standardKey];
      const results = fileData.result;
      resultData[standardKey] = {}; // Initialize the object for this standard

      Object.keys(results).forEach((resultKey) => {
        const resultEntry = results[resultKey];
        if (!resultEntry) return; // Skip null entries
        const { to_analyze, x, peaks } = resultEntry;

        if (to_analyze) {
          const hasComps = peaks && Object.keys(peaks).some(k => Array.isArray(peaks[k]?.y_comp));
          resultData[standardKey][resultKey] = {
            x: [...x],
            y_comp: {},
            x_comp: {}, // per-peak x data (now preferred over range.x)
          };

          // Store each peak's y_comp separately
          if (peaks && Object.keys(peaks).length > 0) {
            Object.keys(peaks).forEach((peakKey) => {
              if (peaks[peakKey].y_comp) {
                resultData[standardKey][resultKey].y_comp[peakKey] = [...peaks[peakKey].y_comp];
              }
              if (Array.isArray(peaks[peakKey].x)) {
                resultData[standardKey][resultKey].x_comp[peakKey] = [...peaks[peakKey].x];
              }
            });
          }
        }
      });
    });

    return resultData;
  }
);


export const selectQcFilesResultData = createSelector(
  [getQcFiles],
  (qcFiles) => {
    const resultData = {};

    Object.keys(qcFiles).forEach((qcKey) => {
      // Handle both old and new file structure
      const fileData = qcFiles[qcKey].fileData || qcFiles[qcKey];
      const results = fileData.result;
      resultData[qcKey] = {}; // Initialize the object for this standard

      Object.keys(results).forEach((resultKey) => {
        const resultEntry = results[resultKey];
        if (!resultEntry) return; // Skip null entries
        const { to_analyze, x, peaks } = resultEntry;

        if (to_analyze) {
          const hasComps = peaks && Object.keys(peaks).some(k => Array.isArray(peaks[k]?.y_comp));
          resultData[qcKey][resultKey] = {
            x: [...x],
            y_comp: {},
            x_comp: {},
          };

          // Store each peak's y_comp separately
          if (peaks && Object.keys(peaks).length > 0) {
            Object.keys(peaks).forEach((peakKey) => {
              if (peaks[peakKey].y_comp) {
                resultData[qcKey][resultKey].y_comp[peakKey] = [...peaks[peakKey].y_comp];
              }
              if (Array.isArray(peaks[peakKey].x)) {
                resultData[qcKey][resultKey].x_comp[peakKey] = [...peaks[peakKey].x];
              }
            });
          }
        }
      });
    });

    return resultData;
  }
);



export const selectUnkFilesResultData = createSelector(
  [getUnkFiles],
  (unkFiles) => {
    const resultData = {};

    Object.keys(unkFiles).forEach((unkKey) => {
      // Handle both old and new file structure
      const fileData = unkFiles[unkKey].fileData || unkFiles[unkKey];
      const results = fileData.result;
      resultData[unkKey] = {}; // Initialize the object for this standard

      Object.keys(results).forEach((resultKey) => {
        const resultEntry = results[resultKey];
        if (!resultEntry) return; // Skip null entries
        const { to_analyze, x, peaks } = resultEntry;

        if (to_analyze) {
          const hasComps = peaks && Object.keys(peaks).some(k => Array.isArray(peaks[k]?.y_comp));
          resultData[unkKey][resultKey] = {
            x: [...x],
            y_comp: {},
            x_comp: {},
          };

          // Store each peak's y_comp separately
          if (peaks && Object.keys(peaks).length > 0) {
            Object.keys(peaks).forEach((peakKey) => {
              if (peaks[peakKey].y_comp) {
                resultData[unkKey][resultKey].y_comp[peakKey] = [...peaks[peakKey].y_comp];
              }
              if (Array.isArray(peaks[peakKey].x)) {
                resultData[unkKey][resultKey].x_comp[peakKey] = [...peaks[peakKey].x];
              }
            });
          }
        }
      });
    });

    return resultData;
  }
);
